import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PropertyProvider } from './contexts/PropertyContext';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

// Guest Pages
import GuestLanding from './pages/guest/GuestLanding';
import EligibilityCheck from './pages/guest/EligibilityCheck';
import InteractiveMap from './pages/guest/InteractiveMap';
import BookingConfirmation from './pages/guest/BookingConfirmation';
import BookingCompletion from './pages/guest/BookingCompletion';
import LegalPage from './pages/guest/LegalPage';
import ManageReservation from './pages/guest/ManageReservation';
import GuestHelp from './pages/guest/GuestHelp';
import AdminHelp from './pages/admin/AdminHelp';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import PropertiesManagement from './pages/admin/PropertiesManagement';
import MapManager from './pages/admin/MapManager';
import InventoryManager from './pages/admin/InventoryManager';
import ReservationsManager from './pages/admin/ReservationsManager';
import IntegrationSettings from './pages/admin/IntegrationSettings';
import BlackoutManager from './pages/admin/BlackoutManager';
import PricingManager from './pages/admin/PricingManager';
import ReportsManager from './pages/admin/ReportsManager';
import UserManagement from './pages/admin/UserManagement';
import InventoryTypesManager from './pages/admin/InventoryTypesManager';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="text-center">
          <div className="inline-block w-16 h-16 border-4 border-[#9F1239] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-lg font-serif text-[#9F1239]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <PropertyProvider>
        <BrowserRouter>
          <div className="App">
            <Routes>
              {/* Guest Routes */}
              <Route path="/" element={<GuestLanding />} />
              <Route path="/check-eligibility" element={<EligibilityCheck />} />
              <Route path="/manage" element={<ManageReservation />} />
              <Route path="/help" element={<GuestHelp />} />
              <Route path="/book" element={<InteractiveMap />} />
              <Route path="/confirmation/:confirmationCode" element={<BookingConfirmation />} />
              <Route path="/booking/complete/:token" element={<BookingCompletion />} />
              <Route path="/sms-terms" element={<LegalPage />} />
              <Route path="/privacy-policy" element={<LegalPage />} />
              <Route path="/legal/:pageType" element={<LegalPage />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/properties"
              element={
                <ProtectedRoute>
                  <PropertiesManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/maps"
              element={
                <ProtectedRoute>
                  <MapManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inventory"
              element={
                <ProtectedRoute>
                  <InventoryManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/help"
              element={
                <ProtectedRoute>
                  <AdminHelp />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reservations"
              element={
                <ProtectedRoute>
                  <ReservationsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute>
                  <IntegrationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blackouts"
              element={
                <ProtectedRoute>
                  <BlackoutManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pricing"
              element={
                <ProtectedRoute>
                  <PricingManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute>
                  <ReportsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inventory-types"
              element={
                <ProtectedRoute>
                  <InventoryTypesManager />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
      </PropertyProvider>
    </AuthProvider>
  );
}

export default App;
